import { Subscription } from 'rxjs';
import { AGUSortOption, AGUViewportService } from 'agu_app_shell';

/**
 * A finder page's phone state, shared by the three finders: whether the surface is on a phone (`AGUViewportService`, live), and
 * whether the filter sheet is open and at which group (the prototype's `html.mfilters-open` and the chip's `g.offsetTop` scroll,
 * P/Member_Hub.html:904, 935). Held by composition on each page; the page calls `Start` in ngOnInit and `Stop` in ngOnDestroy.
 *
 * Leaving the phone layout closes the sheet (`if(!mob) close()`, P/Member_Hub.html:828). The media-query change arrives outside any
 * Angular notification on this zoneless host, so a flip calls the page's repaint.
 */
export class PhoneFinderChrome {
    IsPhone: boolean;
    FiltersOpen = false;
    /** The group the sheet scrolls to on open; null opens it at the top. */
    FilterGroup: string | null = null;

    private subscription: Subscription | null = null;

    constructor(
        private readonly viewport: AGUViewportService | null,
        private readonly repaint: () => void
    ) {
        this.IsPhone = viewport?.IsPhone ?? false;
    }

    Start(): void {
        if (!this.viewport || this.subscription) return;
        this.subscription = this.viewport.IsPhone$.subscribe((isPhone) => {
            if (isPhone === this.IsPhone) return;
            this.IsPhone = isPhone;
            if (!isPhone) this.FiltersOpen = false;
            this.repaint();
        });
    }

    Stop(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
    }

    /** A chip: the Filters chip (null, the sheet's top) or a group chip (P/Member_Hub.html:924, 935). */
    OpenFilters(group: string | null): void {
        this.FilterGroup = group;
        this.FiltersOpen = true;
    }

    /** Done, Escape, the scrim or Show results (P/Member_Hub.html:905-906). */
    CloseFilters(): void {
        this.FiltersOpen = false;
    }
}

/** A page's `{key,label}` sorts in the chrome's `AGUSortOption` shape, for the phone chips' sort select (P/Member_Hub.html:926-932). */
export function ToSortOptions(options: ReadonlyArray<{ key: string; label: string }>): readonly AGUSortOption[] {
    return options.map((option) => ({ Key: option.key, Label: option.label }));
}

/**
 * The rail groups that are narrowing the results right now, for the chips' `.on` state (P/Member_Hub.html:653-654, 939-945): each
 * active-filter pill's key mapped to the rail group label it belongs to, de-duplicated in pill order. Keys with no rail group (the
 * `Show` toggles, judged-query pills) are skipped.
 */
export function ActiveGroupsFrom(pills: ReadonlyArray<{ readonly Key: string }>, groupOfKey: Readonly<Record<string, string>>): readonly string[] {
    const groups: string[] = [];
    for (const pill of pills) {
        const group = groupOfKey[pill.Key];
        if (group !== undefined && !groups.includes(group)) groups.push(group);
    }
    return groups;
}
